import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, of, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
// `Wallpaper` was imported only for the deleted local addToCart() — nothing in
// this service takes one now, because a cart line is built from a priced quote
// rather than from a wallpaper.
import { CartItem, JourneyContext, INITIAL_CONTEXT, NurSignal } from '../models/wallpaper.model';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { getFullAttribution } from '../utils/ga-attribution.util';
import { forwardLine, roundMoney } from '../utils/money.util';

export interface Toast {
  id: number;
  message: string;
  type: 'cart' | 'wishlist' | 'wishlist-remove' | 'success' | 'info';
  exiting?: boolean;
}

export interface WishlistItem {
  id: string;
  name: string;
  image: string;
  price: number;
  wallpaper_type?: string;
  wallpaper_id?: string;
  collection_slug?: string;
  slug?: string;
  product_slug?: string;
  product_type_slug?: string;
}

@Injectable({ providedIn: 'root' })
export class AppStateService {
  private toastIdCounter = 0;
  private cartLoading = false;
  /**
   * Monotonic token for cart reloads. Each loadCartFromApi() call claims the next
   * value and, when its response returns, applies state ONLY if it is still the
   * latest. This is the "newest wins" guard that replaces the old
   * `if (cartLoading) return` early-out — that early-out silently DROPPED a newer
   * reload while an older one was in flight, so after rapid +/- clicks the cart
   * (and the Order Summary derived from it) could stay painted at an older
   * quantity even though every server response was correct.
   */
  private cartReloadSeq = 0;
  /**
   * Cart rows whose DELETE has not finished yet. Filtered out of EVERY reload
   * response until it does: a reload already in flight read the cart before the
   * delete and would otherwise paint the removed line straight back.
   */
  private pendingRemovals = new Set<number>();
  private apiUrl = environment.apiUrl;

  /**
   * Root of the NEW backend. The wallpaper flow runs entirely on it — the cart
   * included — so a wallpaper line is written by the engine that quoted it.
   *
   * The old backend accepted the PDP's `pricing_detail` and then DISCARDED it,
   * recomputing the price on every read through `calculateWallpaperPrice`, which
   * carries a hardcoded rate matrix (`$printRate = 149 / 1.18`). That is why a wall
   * quoted at ₹6,960 on the product page appeared as ₹11,920 on checkout: 80 sq ft
   * × the matrix's ₹149 instead of the ₹87/sq ft the paper chain actually resolves.
   * Everything else on this service still points at `apiUrl` until its own module
   * moves.
   */
  private newApiUrl = environment.newApiUrl;

  cart$ = new BehaviorSubject<CartItem[]>([]);
  cartRaw$ = new BehaviorSubject<any[]>([]);
  cartLoaded$ = new BehaviorSubject<boolean>(false);
  wishlistIds$ = new BehaviorSubject<Set<string>>(new Set());
  wishlistItems$ = new BehaviorSubject<WishlistItem[]>([]);
  wishlistLoaded$ = new BehaviorSubject<boolean>(false);
  context$ = new BehaviorSubject<JourneyContext>({ ...INITIAL_CONTEXT });
  userType$ = new BehaviorSubject<'new' | 'existing'>('new');
  toasts$ = new BehaviorSubject<Toast[]>([]);
  isNurOpen$ = new BehaviorSubject<boolean>(false);
  nurSignal$ = new BehaviorSubject<NurSignal | null>(null);
  showPromoBanner$ = new BehaviorSubject<boolean>(true);

  /**
   * True while a cart mutation (a quantity +/- and the reload it triggers) is in
   * flight, so the cart page can show a blocking spinner and disable its controls
   * until the LATEST cart / Order Summary state has actually been painted. Set by
   * beginCartMutation() and cleared by loadCartFromApi() only when the WINNING
   * reload finishes (success or error) — never merely when the HTTP call returns,
   * so the spinner cannot hide while stale numbers are still on screen.
   */
  cartBusy$ = new BehaviorSubject<boolean>(false);

  constructor(private http: HttpClient) {}

  get cart() { return this.cart$.value; }
  get wishlistIds() { return this.wishlistIds$.value; }
  get wishlistItems() { return this.wishlistItems$.value; }
  get context() { return this.context$.value; }
  get userType() { return this.userType$.value; }

  // ── Auth helpers (read from localStorage to avoid circular dep) ──
  private get authToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  private get authUser(): any {
    try {
      const s = localStorage.getItem('auth_user');
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  }

  private get isLoggedIn(): boolean {
    return !!this.authToken && !!this.authUser;
  }

  get sessionId(): string {
    let sid = localStorage.getItem('session_id');
    if (!sid) {
      sid = AppStateService.newSessionId();
      localStorage.setItem('session_id', sid);
    }
    return sid;
  }

  private static newSessionId(): string {
    const c = (typeof crypto !== 'undefined' ? crypto : undefined) as
      | { randomUUID?: () => string; getRandomValues?: (a: Uint8Array) => Uint8Array }
      | undefined;
    if (c && typeof c.randomUUID === 'function') {
      return c.randomUUID();
    }
    if (c && typeof c.getRandomValues === 'function') {
      const b = c.getRandomValues(new Uint8Array(16));
      b[6] = (b[6] & 0x0f) | 0x40; // version 4
      b[8] = (b[8] & 0x3f) | 0x80; // RFC 4122 variant
      const hex = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => {
      const r = Math.random() * 16 | 0;
      return (ch === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }

  private get authHeaders(): HttpHeaders {
    const h: any = { 'Content-Type': 'application/json' };
    if (this.authToken) h['Authorization'] = `Bearer ${this.authToken}`;
    return new HttpHeaders(h);
  }

  /**
   * Mark the start of a cart mutation (a quantity change). Turns the cart-page
   * spinner on at once; the loadCartFromApi() the caller fires next turns it off
   * only once the freshest state is applied. Kept as its own call so the component
   * never pokes the subject directly.
   */
  beginCartMutation() {
    this.cartBusy$.next(true);
  }

  /** localStorage prefix of the PDP's per-design draft (`pdp_draft_<wallpaperId>`). */
  static readonly PDP_DRAFT_PREFIX = 'pdp_draft_';

  /**
   * Drop the site-service choice (installation / expert visit and the pincode
   * behind it) from every saved PDP draft. Called once an order has gone through.
   *
   * The draft outlives the order — it has no expiry — so a design configured with
   * installation came back with installation ticked on the NEXT order and the add
   * sent `installation: true` without the customer choosing it. Walls, surface and
   * quantity are left alone; only the service fields are cleared, the same fields
   * the PDP's own resetServiceSelection() clears after an add.
   */
  forgetPdpServiceChoices() {
    let keys: string[] = [];
    try {
      keys = Object.keys(localStorage).filter(k => k.startsWith(AppStateService.PDP_DRAFT_PREFIX));
    } catch { return; }
    // Per key, so one unreadable draft does not leave the rest untouched.
    for (const key of keys) {
      try {
        const draft = JSON.parse(localStorage.getItem(key) || 'null');
        if (!draft || typeof draft !== 'object') continue;
        for (const f of ['pincode', 'expertPincode', 'wantInstallation', 'wantExpertVisit',
          'installationAgreed', 'expertVisitBooked', 'expertVisitCartId']) {
          delete draft[f];
        }
        localStorage.setItem(key, JSON.stringify(draft));
      } catch {}
    }
  }

  // ── Cart: single centralized fetch from backend API ──────────────
  loadCartFromApi(force = false) {
    // The old `if (this.cartLoading) return` guard was removed on purpose: it made
    // a reload started while another was in flight a no-op, which is exactly how a
    // newer quantity's reload got dropped and the UI stuck at an older value. A
    // reload in progress must NOT block a fresh one; the seq token below makes the
    // newest response win instead.
    if (this.cartLoaded$.value && !force) {
      // Nothing to fetch — but never leave a mutation spinner hanging.
      this.cartBusy$.next(false);
      return;
    }
    const mySeq = ++this.cartReloadSeq;
    this.cartLoading = true;

    // Both sources are now the NEW backend — the old backend is no longer called
    // for the cart read.
    //
    // `legacy` was the old backend's getCarts: the nested `customizedProduct` shape
    // (items with frame / glass / paper / mount relations, plus a per-unit
    // `pricing_detail`) that the cart page and checkout parse. It has moved to
    // /api/site/cart/legacy, which rebuilds that exact shape from the same tables,
    // SKIPS orphaned rows (the old getCarts 500'd when a cart's customized_product
    // had been deleted), and prices through the shared engine.
    //
    // `site` supplies the money overlay for wallpaper / expert-visit lines (the
    // whole-line figures divided back to per-unit). Kept until the legacy reader
    // folds those in directly; harmless meanwhile, as it only re-states numbers the
    // legacy reader already returns correctly.
    //
    // Neither call sends filter / purchaseType params: the owner is resolved
    // server-side from the bearer token, falling back to the session id.
    const legacy$ = this.http.get<any>(`${this.newApiUrl}/api/site/cart/legacy`, {
      headers: this.authHeaders,
      params: { session_id: this.sessionId },
    }).pipe(catchError(() => of(null)));

    const site$ = this.http.get<any>(`${this.newApiUrl}/api/site/cart`, {
      headers: this.authHeaders,
      params: { session_id: this.sessionId },
    }).pipe(catchError(() => of({ data: [] })));

    forkJoin({
      legacy: legacy$,
      site: site$,
    }).subscribe({
      next: ({ legacy, site }: any) => {
        // Newest-wins: a later reload has already been kicked off, so this (older)
        // response must not overwrite the fresher state it will paint. Leave the
        // spinner on too — the winning reload owns turning it off.
        if (mySeq !== this.cartReloadSeq) return;
        this.cartLoading = false;
        this.cartLoaded$.next(true);
        const rows = legacy?.data || legacy || [];
        // A line being removed stays removed, whatever this snapshot says.
        const data = Array.isArray(rows) ? rows.filter((e: any) => !this.pendingRemovals.has(Number(e?.id))) : rows;

        if (Array.isArray(data) && data.length > 0) {
          const merged = this.withSiteWallpaperPricing(data, site?.data || []);
          this.cartRaw$.next(merged);
          this.cart$.next(merged.map((entry: any) => this.mapCartEntry(entry)));
        } else {
          this.cartRaw$.next([]);
          this.cart$.next([]);
        }
        // State is applied — only now is it safe to hide the mutation spinner.
        this.cartBusy$.next(false);
      },
      error: () => {
        if (mySeq !== this.cartReloadSeq) return;
        this.cartLoading = false;
        this.cartLoaded$.next(true);
        // Clear the spinner on failure too, so a dropped request can never leave
        // the cart permanently blocked behind the overlay.
        this.cartBusy$.next(false);
      }
    });
  }

  /**
   * Replace the money on every WALLPAPER line with the new backend's figures,
   * leaving every other line exactly as the old backend returned it.
   *
   * A translation at the boundary, deliberately: the consumers of `cartRaw$` read
   * the old `pricing_detail` shape, and rewriting them is the checkout migration,
   * not this one. Writing the new numbers into that shape means the cart shows the
   * right money today without touching 1600 lines of checkout. When checkout reads
   * `/api/site/cart` directly, this method goes.
   *
   * THE THING TO GET RIGHT: the old shape is PER UNIT — checkout does
   * `pd.amount_payable * quantity` — while the new backend's figures are for the
   * WHOLE LINE, quantity already inside them. Everything below is divided by the
   * quantity for that reason. Skipping it would multiply a 3-roll line by 3 twice.
   */
  private withSiteWallpaperPricing(entries: any[], siteRows: any[]): any[] {
    if (!Array.isArray(siteRows) || siteRows.length === 0) return entries;
    const byCartId = new Map<number, any>(
      siteRows.map((r: any) => [Number(r.cart_id), r])
    );

    return entries.map((entry: any) => {
      const cp = entry.customizedProduct || {};
      const typeId = Number(cp.product_type_id);
      // 9 = Wall Paper, 24 = Wallpaper Consultation. Both belong to the wallpaper
      // flow and both are now priced by the new backend.
      const isWallpaper = typeId === 9;
      const isVisit = typeId === 24;
      const s = byCartId.get(Number(entry.id));
      if ((!isWallpaper && !isVisit) || !s) return entry;

      if (s.is_sample || s.kind === 'sample') {
        const sampleTotal = Math.round((Number(s.price_incl) || Number(s.total) || 199) * 100) / 100;
        const gst = Number(s.gst_per) || 18;
        const factor = 1 + gst / 100;
        const exTax = Math.round((sampleTotal / factor) * 100) / 100;
        const tax = Math.round((sampleTotal - exTax) * 100) / 100;
        return {
          ...entry,
          customizedProduct: {
            ...cp,
            pricing_detail: {
              ...(cp.pricing_detail || {}),
              is_sample: true,
              sample_type: s.sample_type || 'surface-kit',
              surface_count: s.surface_count || (s.surfaces ? s.surfaces.length : 1),
              surfaces: s.surfaces || [],
              total: exTax,
              total_with_tax: sampleTotal,
              amount_taxable: exTax,
              amount_payable: sampleTotal,
              discount: 0, discount_with_tax: 0, discount_rate: 0,
              tax: tax, tax_rate: gst,
              installation_amount: 0, installation_tax: 0,
              gift_wrap: 0, gift_tax: 0,
            },
            site_has_installation: false,
            site_has_expert_visit: false,
            site_is_sample: true,
            site_surfaces: s.surfaces || [],
            site_line_total: sampleTotal,
          },
        };
      }

      // A booked expert visit: the city's published fee is the BASE and the server
      // adds the service GST on top (business rule, 6 Oct 2026). Both figures come
      // from the server (`visit_fee`, `visit_tax`) — this page never taxes it itself.
      if (isVisit) {
        const r2 = (n: number) => Math.round(n * 100) / 100;
        const fee = r2(Number(s.visit_fee) || Number(s.price) || 0);
        const tax = r2(Number(s.visit_tax) || 0);
        const payable = r2(fee + tax);
        return {
          ...entry,
          customizedProduct: {
            ...cp,
            pricing_detail: {
              ...(cp.pricing_detail || {}),
              // Marks the line as the billed Measurement Service, for the summary.
              is_visit: true,
              total: fee, total_with_tax: payable, amount_taxable: fee, amount_payable: payable,
              discount: 0, discount_with_tax: 0, discount_rate: 0,
              tax, tax_rate: Number(s.gst_per) || 0,
              installation_amount: 0, installation_tax: 0,
              gift_wrap: 0, gift_tax: 0,
            },
            site_has_installation: false,
            site_has_expert_visit: true,
            site_visit_fee: fee,
            site_visit_tax: tax,
            site_install_charge: 0,
            site_service_city: s.service_city || null,
            site_pincode: s.pincode || null,
            site_line_total: Number(s.total) || payable,
          },
        };
      }

      const qty = Math.max(1, Number(cp.quantity) || 1);
      const gst = Number(s.gst_per) || 18;
      const factor = 1 + gst / 100;
      const r2 = (n: number) => Math.round(n * 100) / 100;

      // A line frozen by the FORWARD flow (calc_version 2) is billed exactly so at
      // checkout: ex-GST base → discount off the base → GST added on top. Mirrored
      // here (money.util) so the cart shows the figures Razorpay will collect.
      const fwd = Number(s.calc_version) >= 2
        ? forwardLine(Number(s.price) || 0, Number(s.discount) || 0, gst)
        : null;

      // Whole-line inclusive figures from the server, then per unit.
      const grossInclUnit = fwd ? r2((fwd.base + roundMoney(fwd.base * gst / 100)) / qty) : r2((Number(s.price_incl) || 0) / qty);
      const discInclUnit = r2((Number(s.discount_incl) || 0) / qty);
      const netInclUnit = fwd ? r2(fwd.total / qty) : r2(grossInclUnit - discInclUnit);
      // EARLIER lines: split the INCLUSIVE net rather than re-taxing an ex-tax
      // base — dividing down and multiplying back lands a paisa out.
      const taxableUnit = fwd ? r2(fwd.taxable / qty) : r2(netInclUnit / factor);
      const taxUnit = fwd ? r2(fwd.gst / qty) : r2(netInclUnit - taxableUnit);

      return {
        ...entry,
        customizedProduct: {
          ...cp,
          pricing_detail: {
            ...(cp.pricing_detail || {}),
            total: fwd ? r2(fwd.base / qty) : r2(grossInclUnit / factor),
            total_with_tax: grossInclUnit,
            discount: fwd ? r2(fwd.discount / qty) : r2(discInclUnit / factor),
            discount_with_tax: fwd ? r2(grossInclUnit - netInclUnit) : discInclUnit,
            discount_rate: Number(s.discount_pct) || 0,
            amount_taxable: taxableUnit,
            tax: taxUnit,
            amount_payable: netInclUnit,
            // The slab this line was QUOTED at, not a flat 18 — surfaces sit on
            // different slabs and Glitter is 30%.
            tax_rate: gst,
            printRate: Number(s.rate_incl) || 0,
            // Installation's ex-GST charge (per unit). Its service GST is added
            // ONCE on the order's pooled installation base at checkout, exactly as
            // the server does — so it is not split per line here.
            installation_amount: r2((Number(s.install_charge) || 0) / qty),
            installation_tax: 0,
            // Service GST % (installation, measurement), from the server's config.
            service_gst_per: Number(s.service_gst_per) || 0,
            gift_wrap: 0,
            gift_tax: 0,
          },
          // Which services this line actually holds, stated by the server rather
          // than guessed from a pincode — a visit-only line carries a pincode too.
          site_has_installation: !!s.has_installation,
          site_has_expert_visit: !!s.has_expert_visit,
          site_install_charge: Number(s.install_charge) || 0,
          // ₹/sq ft the installation was quoted at, for the label beside the charge.
          // Per city — the page must never print a rate of its own.
          site_install_rate: Number(s.install_rate) || 0,
          // True when the walls at this pincode pay the fixed small-order charge
          // (total area ≤ the configured sq ft) instead of area × rate.
          site_install_fixed: !!s.install_fixed,
          site_visit_fee: Number(s.visit_fee) || 0,
          site_service_city: s.service_city || null,
          site_pincode: s.pincode || null,
          site_area_sqft: Number(s.area_sqft) || 0,
          // For the summary's rate breakdown: billed sq ft, the ex-GST rate it is
          // multiplied by, and the inclusive display rate (the PDP chip).
          site_billable_sqft: Number(s.billable_sqft) || 0,
          site_rate_ex_gst: Number(s.rate_ex_gst) || 0,
          site_rate_incl: Number(s.rate_incl) || 0,
          site_base: fwd ? fwd.base : 0,
          site_gst_per: gst,
          site_line_total: Number(s.total) || 0,
        },
      };
    });
  }

  // Map a raw API cart entry to a display item with proper pricing
  private mapCartEntry(entry: any): any {
    const cp = entry.customizedProduct || entry;
    let pd = cp.pricing_detail || {};
    if (typeof pd === 'string') { try { pd = JSON.parse(pd); } catch { pd = {}; } }
    const items = cp.customizedProductItems || cp.customized_product_items || [];
    const firstItem = items[0] || {};
    let img = firstItem.image_detail || {};
    if (typeof img === 'string') { try { img = JSON.parse(img); } catch { img = {}; } }

    const cartId = entry.id;                    // cart table ID (for delete API)
    const cpId = cp.id;                         // customized_product ID (for quantity API)
    const quantity = cp.quantity || 1;

    // On-sale items (featured frames/art) have pricing in cp.on_sale
    const onSale = (cp.on_sale_id > 0 && cp.on_sale) ? cp.on_sale : null;

    let amountPayable = 0;
    let listPrice = 0;
    let discountRate = 0;
    let itemName = '';
    let itemImage = '';
    let itemSize = '';

    if (onSale) {
      // Backend on_sale often only has net_price_tax + discount_rate + tax_rate trustworthy.
      // Back-calculate MRP and tax-exclusive bases from those.
      const taxRate = Number(onSale.tax_rate) || 18;
      const factor = 1 + taxRate / 100;
      const osNetTax = Number(onSale.net_price_tax) || 0;
      discountRate = Number(onSale.discount_rate) || Number(onSale.discount) || 0;

      let osListTax = Number(onSale.list_price_tax) || 0;
      if (!osListTax && osNetTax && discountRate > 0 && discountRate < 100) {
        osListTax = Number((osNetTax / (1 - discountRate / 100)).toFixed(2));
      }

      const osNet = Number(onSale.net_price) > 0
        ? Number(onSale.net_price)
        : Number((osNetTax / factor).toFixed(2));
      const osList = Number(onSale.list_price) > 0
        ? Number(onSale.list_price)
        : Number((osListTax / factor).toFixed(2));
      const osTax = Number(onSale.tax) > 0
        ? Number(onSale.tax)
        : Number((osNetTax - osNet).toFixed(2));

      amountPayable = Number(onSale.amount_payable) || osNetTax;
      listPrice = osListTax;
      // Always recompute — backend's discount_with_tax can be wrong (sometimes negative).
      const osDiscountWithTax = Number((osListTax - amountPayable).toFixed(2));
      const osDiscount = Number((osList - osNet).toFixed(2));
      itemName = onSale.title || onSale.caption || cp.name || 'Featured Frame';
      itemImage = onSale.list_thumbnail || onSale.hover_thumbnail || onSale.image_1 || img.image_path || cp.image || '';
      itemSize = onSale.final_size || firstItem.final_size || firstItem.image_size || '';

      pd = {
        total: osList,
        total_with_tax: osListTax,
        discount: osDiscount,
        discount_with_tax: osDiscountWithTax,
        discount_rate: discountRate,
        amount_taxable: osNet,
        tax: osTax,
        tax_rate: taxRate,
        amount_payable: amountPayable,
        gift_wrap: Number(onSale.gift_wrap) || 0,
        gift_tax: Number(onSale.gift_tax) || 0,
        installation_amount: Number(onSale.installation_amount) || 0,
        installation_tax: Number(onSale.installation_tax) || 0,
      };
    } else {
      // Custom product: prices come from pricing_detail
      amountPayable = pd.amount_payable || pd.total_with_tax || 0;
      listPrice = pd.total_with_tax || pd.total || 0;
      discountRate = pd.discount_rate || 0;
      // Distinguish product types so the mini-cart/header don't label every
      // non-on_sale item as "Wallpaper".
      const isCollageSet = (cp.product_id === 2) || (cp.collage_layout_id || 0) > 0;
      const collageLayoutObj = cp.collage_layout || cp.collageLayout || null;
      const isConsultation = cp.url && cp.url.includes('product=consultation');
      const isSample = pd.is_sample || (cp.url && cp.url.includes('sampleType=surface-kit'));
      const isFrameUpload = cp.url && cp.url.includes('product=frame_your_photos');
      if (isCollageSet) {
        itemName = collageLayoutObj?.caption || img.image_caption || cp.name || 'Collage Set';
        itemImage = collageLayoutObj?.list_thumbnail || img.image_path || cp.image || '';
      } else if (isConsultation) {
        itemName = 'Wall Measurement by an Expert';
        itemImage = img.image_path || cp.image || '';
      } else if (isSample) {
        itemName = (img.image_caption || cp.name || 'Wallpaper') + ' (Sample Kit)';
        itemImage = img.image_path || cp.image || '';
      } else if (isFrameUpload) {
        itemName = img.image_caption || cp.name || 'Photo Frame';
        itemImage = img.image_path || cp.image || '';
      } else {
        itemName = img.image_caption || cp.name || 'Wallpaper';
        itemImage = img.image_path || cp.image || '';
      }
      itemSize = firstItem.image_size || firstItem.final_size || '';
    }

    return {
      id: cartId || cpId,
      cart_id: cartId,
      customized_product_id: cpId,
      on_sale_id: cp.on_sale_id || 0,
      on_sale: onSale,
      name: itemName,
      image: itemImage,
      price: amountPayable,
      list_price: listPrice,
      discount_rate: discountRate,
      quantity: quantity,
      slug: cp.url || '',
      collection: '',
      sku: img?.image_id || cp.wallpaper_id || '',
      category: '',
      pricing_data: pd,
      surface_name: (pd.surfaces && Array.isArray(pd.surfaces) && pd.surfaces.length > 0)
        ? pd.surfaces.join(', ')
        : (firstItem.paper?.display_name || firstItem.paper?.name || ''),
      roll_size: cp.roll_size || firstItem.roll_size || itemSize,
      size_label: itemSize,
    };
  }

  /**
   * Add a configured WALLPAPER to the cart, on the new backend.
   *
   * The payload is the clean contract the panel already uses — the surface, the
   * walls, and the two service booleans — and the server resolves every rupee from
   * them. Nothing about price is sent, which is the point: the old endpoint took a
   * `pricing_detail` from the browser, threw it away, and recomputed from a
   * hardcoded matrix, so the cart could disagree with the page that filled it.
   *
   * `installation` and `expert_visit` are stated outright rather than inferred from
   * the pincode. Both services are priced per city so both need one; inferring
   * would mark BOTH and the server rejects that pair.
   *
   * Returns `cart_ids` — one per wall — which Buy Now scopes itself to.
   */
  apiAddWallpaperToCart(payload: {
    wallpaper_id: number;
    paper_id: number;
    quantity: number;
    walls: Array<{ width: number; height: number }>;
    installation?: boolean;
    expert_visit?: boolean;
    pincode?: string;
  }) {
    return this.http.post(`${this.newApiUrl}/api/site/cart/wallpaper`, {
      ...payload,
      // Sent even when logged in: the server prefers the bearer token and only
      // falls back to the session, so this costs nothing and keeps a guest cart
      // working if the token has expired.
      session_id: this.sessionId,
    }, { headers: this.authHeaders });
  }

  /**
   * Book a standalone expert visit — a measurement with no wallpaper attached.
   *
   * On the new backend the fee comes from the city's own `visit_charge`. The old
   * path built a whole fake `customizedProduct` (product_type 24, a hand-written
   * caption, `price: 999`) and the backend then charged it through
   * `SamplePriceObject`, which treats 999 as EX-tax and adds GST — ₹1,178.82 for a
   * visit the panel prices at ₹1,000.
   */
  apiBookExpertVisit(pincode: string, wallpaperId?: number) {
    return this.http.post(`${this.newApiUrl}/api/site/cart/expert-visit`, {
      pincode,
      ...(wallpaperId ? { wallpaper_id: wallpaperId } : {}),
      session_id: this.sessionId,
    }, { headers: this.authHeaders });
  }

  /**
   * Add or drop installation / an expert visit on a line already in the cart, and
   * let the SERVER price it.
   *
   * What the checkout page needed and never had: its checkbox set a flag in memory
   * and added `area × 20 × quantity` to the displayed total — a rate that is ₹13 in
   * Ghaziabad and ₹21 elsewhere, and that vanished on reload because nothing was
   * written to the row.
   */
  apiUpdateCartServices(cartId: number, opts: { installation?: boolean; expert_visit?: boolean; pincode?: string }) {
    return this.http.patch(`${this.newApiUrl}/api/site/cart/${cartId}/services`, {
      installation: !!opts.installation,
      expert_visit: !!opts.expert_visit,
      ...(opts.pincode ? { pincode: opts.pincode } : {}),
      session_id: this.sessionId,
    }, { headers: this.authHeaders });
  }

  /**
   * Order a sample swatch kit on the new backend.
   *
   * Replaces the old customizedProduct hack with URL query params.
   * Priced server-side: ₹199 for 1st surface, +₹50 per additional surface.
   */
  /** The sample kit price rule (first surface, each extra) — from the server. */
  apiGetSamplePricing() {
    return this.http.get<any>(`${this.newApiUrl}/api/site/samples/pricing`);
  }

  apiAddSampleToCart(payload: { wallpaper_id: number; paper_ids: number[] }) {
    return this.http.post(`${this.newApiUrl}/api/site/cart/sample`, {
      wallpaper_id: payload.wallpaper_id,
      paper_ids: payload.paper_ids,
      session_id: this.sessionId,
    }, { headers: this.authHeaders });
  }

  // ── Cart: add to cart via backend API ─────────────────────────────
  // Still the OLD backend, and still correct: frames, collage sets and on-sale art
  // have not been migrated. The whole WALLPAPER flow has — wallpaper lines go
  // through apiAddWallpaperToCart and visits through apiBookExpertVisit.
  apiAddToCart(customizedProduct: any) {
    const attribution = getFullAttribution();

    // Print & Frame (product 2) now adds on the NEW backend: it re-prices the line
    // server-side (the browser's price is never trusted) and writes the same
    // customized_product / item / cart rows the old backend did. Both logged-in and
    // guest hit the one route — a token, when present, wins over the session id in
    // the controller. Wall-art, collage and featured still add on the old backend
    // until their own migration lands.
    if (Number(customizedProduct?.product_id) === 2) {
      return this.http.post(`${this.newApiUrl}/api/site/cart/frame`,
        { customizedProduct, session_id: this.sessionId, ...attribution },
        { headers: this.authHeaders });
    }

    if (this.isLoggedIn) {
      return this.http.post(`${this.apiUrl}/api/auth/cart`, { customizedProduct, ...attribution }, { headers: this.authHeaders });
    }
    return this.http.post(`${this.apiUrl}/api/unauth/cart_guest`, { session_id: this.sessionId, customizedProduct, ...attribution });
  }

  // ── Cart: remove item via backend API + update local state ────────
  apiRemoveCartItem(cartItemId: number) {
    const id = Number(cartItemId);
    // Hidden from every reload until the DELETE finishes, and any reload already
    // in flight is superseded — its snapshot predates the delete. Without this a
    // removal "sometimes" came back: whenever the click beat a pending reload.
    this.pendingRemovals.add(id);
    this.cartReloadSeq++;

    // Remove from local state immediately
    this.cart$.next(this.cart.filter(item => String(item.id) !== String(id) && String((item as any).cart_id) !== String(id)));
    this.cartRaw$.next(this.cartRaw$.value.filter((entry: any) => Number(entry.id) !== id));

    // New backend. It scopes the delete to the requesting owner (token, else
    // session), so a cart id from someone else's session cannot be removed by
    // guessing the number — the old endpoints took an id list and a session id as
    // separate, unrelated parameters.
    return this.http.delete(`${this.newApiUrl}/api/site/cart/${id}`, {
      headers: this.authHeaders,
      params: { session_id: this.sessionId },
    }).pipe(
      // 404 = the row is already gone (a second click): that IS removed.
      catchError(err => err?.status === 404 ? of({ message: 'Already removed.' }) : throwError(err)),
      // Success or failure, re-read the server so the screen shows what is
      // really in the cart — a failed delete brings the line back honestly.
      finalize(() => {
        this.pendingRemovals.delete(id);
        this.loadCartFromApi(true);
      }),
    );
  }

  // ── Cart: update quantity via backend API ─────────────────────────
  /**
   * Change a line's quantity, on the new backend.
   *
   * Takes the CART id, not the customized_product id — the old endpoint keyed off
   * the latter, which is why the caller had to carry both. The server rescales the
   * stored line price, which is a whole-line figure, so the order bills the new
   * quantity rather than multiplying an already-multiplied amount.
   */
  apiUpdateQuantity(cartId: number, quantity: number) {
    return this.http.patch(`${this.newApiUrl}/api/site/cart/${cartId}`, {
      quantity,
      session_id: this.sessionId,
    }, { headers: this.authHeaders });
  }

  // ── Toast ────────────────────────────────────────────────────────
  showToast(message: string, type: Toast['type'] = 'info') {
    const id = ++this.toastIdCounter;
    const current = this.toasts$.value.slice(-3);
    this.toasts$.next([...current, { id, message, type }]);
    setTimeout(() => {
      this.toasts$.next(this.toasts$.value.map(t => t.id === id ? { ...t, exiting: true } : t));
      setTimeout(() => {
        this.toasts$.next(this.toasts$.value.filter(t => t.id !== id));
      }, 350);
    }, 2800);
  }

  dismissToast(id: number) {
    this.toasts$.next(this.toasts$.value.map(t => t.id === id ? { ...t, exiting: true } : t));
    setTimeout(() => {
      this.toasts$.next(this.toasts$.value.filter(t => t.id !== id));
    }, 350);
  }

  // ── Context ──────────────────────────────────────────────────────
  updateContext(update: Partial<JourneyContext>) {
    this.context$.next({ ...this.context, ...update });
  }


  setUserType(type: 'new' | 'existing') {
    this.userType$.next(type);
    if (type === 'new') {
      this.context$.next({ ...INITIAL_CONTEXT });
    }
  }

  // ── Wishlist ─────────────────────────────────────────────────────
  toggleWishlist(id: string, itemData?: WishlistItem) {
    const next = new Set(this.wishlistIds);
    const adding = !next.has(id);
    if (next.has(id)) {
      next.delete(id);
      const removed = this.wishlistItems.find(w => w.id === id);
      const name = removed ? removed.name.split(' ').slice(0, 3).join(' ') : 'Item';
      this.wishlistItems$.next(this.wishlistItems.filter(w => w.id !== id));
      this.showToast(`${name} removed from wishlist`, 'wishlist-remove');
    } else {
      next.add(id);
      if (itemData && !this.wishlistItems.find(w => w.id === id)) {
        this.wishlistItems$.next([...this.wishlistItems, itemData]);
      }
      const name = itemData ? itemData.name.split(' ').slice(0, 3).join(' ') : 'Item';
      this.showToast(`${name} added to wishlist`, 'wishlist');
    }
    this.wishlistIds$.next(next);
    // Persist to the server for logged-in customers (shared with the admin
    // panel — sales staff and the customer see the same wishlist).
    this.syncWishlistToServer(id, adding);
  }

  /**
   * Load the logged-in customer's wishlist from the server so it persists
   * across sessions and reflects items added by sales staff in the admin
   * panel. Any in-session (guest) items are merged up to the server first.
   */
  loadWishlistFromApi() {
    if (!this.isLoggedIn) {
      // Guest: the in-memory wishlist is authoritative, nothing to fetch.
      this.wishlistLoaded$.next(true);
      return;
    }

    const fetch = () => {
      this.http.get(`${this.apiUrl}/api/auth/new-wishlist`, { headers: this.authHeaders }).subscribe({
        next: (res: any) => {
          const data = Array.isArray(res?.data) ? res.data : [];
          const items: WishlistItem[] = data.map((w: any) => ({
            id: String(w.id),
            name: w.name ?? 'Wallpaper',
            image: w.thumbnail ?? w.list_thumbnail ?? '',
            price: Number(w.price) || 0,
            collection_slug: w.collection?.collection_slug,
            slug: w.slug,
          }));
          this.wishlistItems$.next(items);
          this.wishlistIds$.next(new Set(items.map(i => i.id)));
          this.wishlistLoaded$.next(true);
        },
        error: () => this.wishlistLoaded$.next(true),
      });
    };

    // Merge any items added before login into the server, then load.
    const localIds = Array.from(this.wishlistIds).map(Number).filter(Boolean);
    if (localIds.length) {
      this.http.post(`${this.apiUrl}/api/auth/new-wishlist-sync`, { wallpaper_ids: localIds }, { headers: this.authHeaders })
        .subscribe({ next: fetch, error: fetch });
    } else {
      fetch();
    }
  }

  /** Persist a single wishlist add/remove to the server (logged-in only). */
  private syncWishlistToServer(wallpaperId: string, add: boolean) {
    if (!this.isLoggedIn) return;
    const idNum = Number(wallpaperId);
    if (!idNum) return;
    if (add) {
      this.http.post(`${this.apiUrl}/api/auth/new-wishlist`, { wallpaper_id: idNum }, { headers: this.authHeaders })
        .subscribe({ next: () => {}, error: () => {} });
    } else {
      this.http.delete(`${this.apiUrl}/api/auth/new-wishlist/${idNum}`, { headers: this.authHeaders })
        .subscribe({ next: () => {}, error: () => {} });
    }
  }

  /*
   * ── REMOVED: addToCart / updateCartQuantity / removeFromCart ──────────
   *
   * Three local-only cart mutators, left over from when this cart lived in
   * memory. Each one wrote straight into `cart$` and sent NO request, so every
   * button behind them edited a picture of the cart rather than the cart:
   *
   *   cart page  +/-        quantity moved on screen, nothing saved
   *   cart page  remove     item returned on the next load
   *   cart page  "Add"      pushed a MOCK_WALLPAPERS row in, unpriced
   *   home page  "Reorder"  badge +1, nothing reordered
   *   checkout   move to wishlist   wishlisted AND still billed
   *
   * They were also why the header badge could be watched changing on its own: a
   * local guess would land first and `loadCartFromApi()` would overwrite it with
   * the real cart a moment later, which reads as data corrupting itself.
   *
   * Every caller now goes through the API — apiUpdateQuantity,
   * apiRemoveCartItem, or the PDP, which is the only place that can build a
   * priced line. Deleted rather than deprecated so a future caller cannot
   * reintroduce a cart that disagrees with the database.
   */

  toggleNur() {
    this.isNurOpen$.next(true);
  }

  closeNur() {
    this.isNurOpen$.next(false);
  }

  emitNurSignal(signal: NurSignal) {
    this.nurSignal$.next(signal);
    // Auto-clear after handling
    setTimeout(() => this.nurSignal$.next(null), 100);
  }

}
